﻿import { Feature } from 'geojson';
import { useCallback, useRef } from 'react';
import { useEvent } from '../../../contexts/event-bus';
import useMap from '../../../hooks/use-map';

export type PreviewInfo = {
    id: string;
    coordinates: number[][];
};

const PREFIX = 'fe-preview';
const DEFAULT_STYLE = { color: '#5e5754', fillColor: '#8f7f79', fillOpacity: 0.3, weight: 2 };

export function PreviewProvider() {
    const previewIds = useRef<string[]>([]);
    const map = useMap();

    const addPreview = useCallback(
        (preview: PreviewInfo) => {
            if (!map) {
                return;
            }

            const { id, coordinates } = preview;
            const combinedId = `${PREFIX}-${id}`;

            if (!map.getSource(combinedId)) {
                map.addSource(combinedId, {
                    type: 'geojson',
                    data: {
                        type: 'Feature',
                        geometry: {
                            type: 'Polygon',
                            coordinates: [coordinates]
                        }
                    } as Feature
                });
            }

            map.addLayer({
                id: `${combinedId}-fill`,
                type: 'fill',
                source: combinedId,
                paint: {
                    'fill-color': DEFAULT_STYLE.fillColor,
                    'fill-opacity': DEFAULT_STYLE.fillOpacity
                }
            });

            map.addLayer({
                id: `${combinedId}-line`,
                type: 'line',
                source: combinedId,
                paint: {
                    'line-color': DEFAULT_STYLE.color,
                    'line-width': DEFAULT_STYLE.weight
                }
            });

            previewIds.current.push(id);
        },
        [map]
    );

    const removePreview = useCallback(
        (id: string) => {
            if (!map) {
                return;
            }

            const combinedId = `${PREFIX}-${id}`;

            if (map.getLayer(`${combinedId}-fill`)) {
                map.removeLayer(`${combinedId}-fill`);
            }
            if (map.getLayer(`${combinedId}-line`)) {
                map.removeLayer(`${combinedId}-line`);
            }
            if (map.getSource(combinedId)) {
                map.removeSource(combinedId);
            }
            
            previewIds.current.splice(previewIds.current.indexOf(id), 1);
        },
        [map]
    );
    
    const removeAllPreviews = useCallback(() => {
        previewIds.current.forEach((id) => removePreview(id));
        previewIds.current = [];
    }, [removePreview]);

    useEvent('show-preview', addPreview);

    useEvent('hide-preview', (e) => removePreview(e.id));
    
    useEvent('hide-all-previews', () => removeAllPreviews());

    return null;
}
